/**
 * CloseShiftButton — bottom-of-Today-tab control for cashiers AND operators.
 *
 * Click → confirm dialog showing the snapshot (loads · cashouts · freeplay · net) →
 *   "Close Shift" commits to /api/shifts/close. Snapshot is frozen forever.
 *   A PDF receipt opens in a new tab. Recent closes are listed below.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    FileCheck2, Loader2, Download, X, Banknote,
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' CT' : '—';

export default function CloseShiftButton() {
    const [open, setOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [closing, setClosing] = useState(false);
    const [recent, setRecent] = useState([]);

    const loadRecent = async () => {
        try {
            const { data } = await axios.get(`${API}/shifts/recent`, { withCredentials: true });
            setRecent(data || []);
        } catch { /* silent */ }
    };

    useEffect(() => { loadRecent(); }, []);

    const openDialog = async () => {
        setOpen(true);
        setLoadingPreview(true);
        try {
            const { data } = await axios.get(`${API}/shifts/preview`, { withCredentials: true });
            setPreview(data);
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load preview'); }
        finally { setLoadingPreview(false); }
    };

    const doClose = async () => {
        setClosing(true);
        try {
            const { data } = await axios.post(`${API}/shifts/close`, {}, { withCredentials: true });
            toast.success('Shift closed · PDF receipt opening');
            window.open(`${BACKEND_BASE}${data.pdf_url}`, '_blank', 'noopener,noreferrer');
            setOpen(false);
            setPreview(null);
            loadRecent();
        } catch (e) { toast.error(e.response?.data?.detail || 'Close failed'); }
        finally { setClosing(false); }
    };

    return (
        <div data-testid="close-shift-section">
            <div className="flex justify-end">
                <Button onClick={openDialog}
                    className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold"
                    data-testid="close-shift-btn">
                    <FileCheck2 className="w-4 h-4 mr-2" />
                    Close Shift &amp; Get PDF
                </Button>
            </div>

            {recent.length > 0 && (
                <div className="card-dark p-3 mt-3">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Recent shift closes</p>
                    <div className="divide-y divide-zinc-800">
                        {recent.slice(0, 5).map((s) => (
                            <div key={s.id} className="py-2 flex items-center gap-2 text-xs" data-testid={`recent-shift-${s.id}`}>
                                <div className="flex-1 min-w-0">
                                    <p className="text-zinc-300 truncate">{fmtTime(s.closed_at)} · <span className="text-emerald-300 font-mono">{fmt(s.loads?.total)}</span> in / <span className="text-rose-300 font-mono">{fmt(s.cashouts?.total)}</span> out</p>
                                    <p className="text-[10px] text-zinc-600">Net <span className={`font-mono ${s.net >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{fmt(s.net)}</span></p>
                                </div>
                                <a href={`${BACKEND_BASE}${s.pdf_url}`} target="_blank" rel="noopener noreferrer"
                                    className="text-amber-300 hover:text-amber-200 p-1"
                                    data-testid={`shift-pdf-${s.id}`}>
                                    <Download className="w-4 h-4" />
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setPreview(null); } }}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md text-zinc-100" data-testid="close-shift-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <FileCheck2 className="w-5 h-5 text-amber-400" /> Close shift?
                        </DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Snapshot freezes today&apos;s numbers. Future activity goes onto the next shift.
                        </DialogDescription>
                    </DialogHeader>
                    {loadingPreview ? (
                        <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-500" /></div>
                    ) : preview ? (
                        <div className="space-y-2 py-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-zinc-400">Loads</span>
                                <span className="text-emerald-300 font-mono font-bold">{fmt(preview.loads?.total)} <span className="text-[10px] text-zinc-500">({preview.loads?.count})</span></span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-zinc-400">Cashouts</span>
                                <span className="text-rose-300 font-mono font-bold">{fmt(preview.cashouts?.total)} <span className="text-[10px] text-zinc-500">({preview.cashouts?.count})</span></span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-zinc-400">Free play given</span>
                                <span className="text-amber-300 font-mono">{fmt(preview.freeplay?.total)}</span>
                            </div>
                            <div className="flex justify-between text-base pt-2 border-t border-zinc-800">
                                <span className="text-zinc-300 font-bold flex items-center gap-1"><Banknote className="w-4 h-4 text-amber-300" /> Net</span>
                                <span className={`font-mono font-bold ${preview.net >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{fmt(preview.net)}</span>
                            </div>
                        </div>
                    ) : null}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setOpen(false)} disabled={closing}
                            className="border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800">
                            <X className="w-4 h-4 mr-1" /> Cancel
                        </Button>
                        <Button onClick={doClose} disabled={closing || loadingPreview}
                            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold"
                            data-testid="close-shift-confirm">
                            {closing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileCheck2 className="w-4 h-4 mr-1" /> Close & download PDF</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
